import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPending, getMenuItems, previewDish, confirmDish, confirmDishBatch, skipDish } from './notInMenuApi';
import MenuItemPicker from './MenuItemPicker';
import DishPreview from './DishPreview';
import UndoDishButton from './UndoDishButton';
import UndoDishConfirm from './UndoDishConfirm';
import ShortageConsentModal, { normaliseShortages } from '../shared/ShortageConsentModal';
import './NotInMenu.css';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const SECTIONS = [
  { key: 'pending', title: 'To map' },
  { key: 'mapped', title: 'Mapped — confirm to deduct' },
  { key: 'waiting', title: 'Mapped — waiting for the closing count' },
  { key: 'revenue_booked', title: 'Revenue booked — stock still to deduct' },
];
const sectionOf = (g) => (g.state === 'mapped' && g.waiting ? 'waiting' : g.state);
// A name can wait in two groups at once (a restaurant bill and a banquet), so a card is
// keyed by its group too (spec 2026-10-26-tsr-add-as-not-in-menu).
const cardKey = (g) => `${g.group || 'sales'}:${g.group_key}`;
const LANES = [['regular', 'Regular'], ['online', 'Online'], ['banquet', 'Banquet']];
const lanesOf = (g) => {
  const present = new Set((g.rows || []).map((r) => r.lane || 'regular'));
  if (present.size === 0) present.add(g.group === 'banquet' ? 'banquet' : 'regular');
  return LANES.filter(([lane]) => present.has(lane));
};

/**
 * Not in Menu: bill lines that matched no menu item. Pick the dish each was,
 * check the preview, confirm — the stock comes off straight away (spec D1).
 * Mounted by Sales Report and by the Closing Stock gate.
 *
 * onProceed (Closing Stock only): the footer's "Confirm & Proceed". It must
 * resolve { ok: true } once the server's gate clears (the caller then moves on),
 * or { ok: false, error, dishes } to keep the worklist open with the reason.
 */
export default function NotInMenuWorklist({ open, onClose, onChanged, onProceed }) {
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoadingKey, setPreviewLoadingKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [shortages, setShortages] = useState(null);
  const [notice, setNotice] = useState(null);
  const [undoFor, setUndoFor] = useState(null);
  const [filter, setFilter] = useState('');
  const [proceeding, setProceeding] = useState(false);
  const [proceedError, setProceedError] = useState(null);
  const [stagedMappings, setStagedMappings] = useState({});
  const [selectedCardKeys, setSelectedCardKeys] = useState(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);
  // A ref, not state: two clicks in one tick both see proceeding === false.
  const proceedingRef = useRef(false);
  const previewCacheRef = useRef({});

  const load = useCallback(async () => {
    setLoadError(null);
    const [p, m] = await Promise.all([getPending(), getMenuItems()]);
    if (!p.ok) { setData(null); setLoadError(p.error); return; }
    setData(p);
    if (m.ok) setMenu(m.data || m.items || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActive(null); setPreview(null); setPreviewLoadingKey(null); setNotice(null); setError(null); setProceedError(null);
    setStagedMappings({}); setSelectedCardKeys(new Set()); previewCacheRef.current = {};
    load();
  }, [open, load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  const pick = async (group, item) => {
    const k = cardKey(group);
    if (active === k && (preview || previewLoadingKey === k)) {
      setActive(null);
      setPreview(null);
      setPreviewLoadingKey(null);
      return;
    }
    const menuItemId = item.menu_item_id ?? item.id;
    const groupType = group.group || 'sales';
    const cacheKey = `${group.group_key}_${menuItemId}_${groupType}`;

    setActive(k);
    setError(null);

    if (previewCacheRef.current[cacheKey]) {
      setPreview(previewCacheRef.current[cacheKey]);
      setPreviewLoadingKey(null);
      return;
    }

    setPreview(null);
    setPreviewLoadingKey(k);
    const r = await previewDish(group.group_key, menuItemId, groupType);
    setPreviewLoadingKey((cur) => (cur === k ? null : cur));
    if (!r.ok) { setError(r.error); return; }
    previewCacheRef.current[cacheKey] = r;
    setPreview(r);
  };

  const stageMapping = (group, item) => {
    const k = cardKey(group);
    setStagedMappings((prev) => ({
      ...prev,
      [k]: {
        groupKey: group.group_key,
        menuItemId: item.menu_item_id ?? item.id,
        menuItemName: item.name,
        group: group.group || 'sales',
        itemName: group.item_name,
      },
    }));
    setSelectedCardKeys((prev) => new Set(prev).add(k));
  };

  const unstageMapping = (group) => {
    const k = cardKey(group);
    setStagedMappings((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const toggleCardSelection = (group) => {
    const k = cardKey(group);
    setSelectedCardKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
      } else {
        next.add(k);
        if (!stagedMappings[k]) {
          if (group.resolved_menu_item_id && group.resolved_menu_item_name) {
            setStagedMappings((staged) => ({
              ...staged,
              [k]: {
                groupKey: group.group_key,
                menuItemId: group.resolved_menu_item_id,
                menuItemName: group.resolved_menu_item_name,
                group: group.group || 'sales',
                itemName: group.item_name,
              },
            }));
          } else if (group.suggestions && group.suggestions.length > 0) {
            const top = group.suggestions[0];
            setStagedMappings((staged) => ({
              ...staged,
              [k]: {
                groupKey: group.group_key,
                menuItemId: top.menu_item_id,
                menuItemName: top.name,
                group: group.group || 'sales',
                itemName: group.item_name,
              },
            }));
          }
        }
      }
      return next;
    });
  };

  const autoSelectSuggestions = (list) => {
    const nextStaged = { ...stagedMappings };
    const nextSelected = new Set(selectedCardKeys);
    let mappedCount = 0;
    for (const g of list) {
      const k = cardKey(g);
      if (nextStaged[k]) {
        nextSelected.add(k);
        continue;
      }
      if (g.resolved_menu_item_id && g.resolved_menu_item_name) {
        nextStaged[k] = {
          groupKey: g.group_key,
          menuItemId: g.resolved_menu_item_id,
          menuItemName: g.resolved_menu_item_name,
          group: g.group || 'sales',
          itemName: g.item_name,
        };
        nextSelected.add(k);
        mappedCount++;
      } else if (g.suggestions && g.suggestions.length > 0) {
        const top = g.suggestions[0];
        nextStaged[k] = {
          groupKey: g.group_key,
          menuItemId: top.menu_item_id,
          menuItemName: top.name,
          group: g.group || 'sales',
          itemName: g.item_name,
        };
        nextSelected.add(k);
        mappedCount++;
      }
    }
    setStagedMappings(nextStaged);
    setSelectedCardKeys(nextSelected);
    setNotice(
      mappedCount > 0
        ? `Auto-selected suggestions for ${mappedCount} dish${mappedCount === 1 ? '' : 'es'}. Click "Confirm Mapped" to confirm them all.`
        : 'All available suggestions have already been selected.'
    );
  };

  const toggleSelectAll = (list) => {
    const allKeys = list.map(cardKey);
    const areAllSelected = allKeys.length > 0 && allKeys.every((k) => selectedCardKeys.has(k));
    if (areAllSelected) {
      setSelectedCardKeys((prev) => {
        const next = new Set(prev);
        allKeys.forEach((k) => next.delete(k));
        return next;
      });
    } else {
      const nextStaged = { ...stagedMappings };
      const nextSelected = new Set(selectedCardKeys);
      for (const g of list) {
        const k = cardKey(g);
        nextSelected.add(k);
        if (!nextStaged[k]) {
          if (g.resolved_menu_item_id && g.resolved_menu_item_name) {
            nextStaged[k] = {
              groupKey: g.group_key,
              menuItemId: g.resolved_menu_item_id,
              menuItemName: g.resolved_menu_item_name,
              group: g.group || 'sales',
              itemName: g.item_name,
            };
          } else if (g.suggestions && g.suggestions.length > 0) {
            const top = g.suggestions[0];
            nextStaged[k] = {
              groupKey: g.group_key,
              menuItemId: top.menu_item_id,
              menuItemName: top.name,
              group: g.group || 'sales',
              itemName: g.item_name,
            };
          }
        }
      }
      setStagedMappings(nextStaged);
      setSelectedCardKeys(nextSelected);
    }
  };

  const confirm = async (allowNegativeStock = false) => {
    if (!preview) return;
    setBusy(true); setError(null);
    const r = await confirmDish({ groupKey: preview.group_key, menuItemId: preview.menu_item.id, allowNegativeStock, group: preview.group || 'sales' });
    setBusy(false);
    if (!r.ok) {
      if (r.code === 'COMPLIMENTARY_SHORTAGE') {
        const seen = new Set();
        const all = [...(preview.shortages || []), ...(r.shortages || [])].filter((x) => {
          const k = `${x.menuItemName || x.itemName || ''}|${x.ingredientId ?? x.ingredientName}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setShortages(all);
        return;
      }
      setShortages(null); setError(r.error); return;
    }
    setShortages(null);
    setNotice(r.status === 'waiting'
      ? `${preview.item_name}: saved. Its stock settles when the closing count is applied.`
      : `${preview.item_name}: deducted ${r.deducted_lines} line(s)${r.absorbed_lines ? `, ${r.absorbed_lines} already counted` : ''} · ${
        preview.group === 'banquet' ? 'added to the banquet\'s dishes' : `${money(r.promoted_amount)} added to sales`}.`);
    const key = `${preview.group || 'sales'}:${preview.group_key}`;
    setStagedMappings((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSelectedCardKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    setPreview(null); setActive(null);
    changed();
  };

  const confirmBatch = async (allowNegativeStock = false) => {
    const itemsToConfirm = Object.entries(stagedMappings)
      .filter(([k]) => selectedCardKeys.has(k))
      .map(([, val]) => val);

    if (itemsToConfirm.length === 0) {
      setError('Please select at least one dish that has been mapped to a menu item.');
      return;
    }

    setBusy(true);
    setBatchProcessing(true);
    setError(null);

    const r = await confirmDishBatch({
      items: itemsToConfirm.map((it) => ({
        groupKey: it.groupKey,
        menuItemId: it.menuItemId,
        group: it.group,
      })),
      allowNegativeStock,
    });

    setBusy(false);
    setBatchProcessing(false);

    if (!r.ok) {
      if (r.code === 'COMPLIMENTARY_SHORTAGE') {
        const seen = new Set();
        const allShortages = (r.shortages || []).filter((x) => {
          const k = `${x.menuItemName || x.itemName || ''}|${x.ingredientId ?? x.ingredientName}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setShortages(allShortages);
        return;
      }
      setShortages(null);
      setError(r.error);
      return;
    }

    setShortages(null);
    setNotice(
      `Successfully mapped and confirmed ${r.confirmedCount} dish${r.confirmedCount === 1 ? '' : 'es'}${r.absorbedLines ? ` (${r.absorbedLines} absorbed)` : ''} · ${money(r.promotedAmount)} added to sales.`
    );
    const confirmedKeys = new Set(itemsToConfirm.map((it) => `${it.group || 'sales'}:${it.groupKey}`));
    setStagedMappings((prev) => {
      const next = { ...prev };
      confirmedKeys.forEach((k) => delete next[k]);
      return next;
    });
    setSelectedCardKeys((prev) => {
      const next = new Set(prev);
      confirmedKeys.forEach((k) => next.delete(k));
      return next;
    });
    changed();
  };

  const skip = async (groupKey, unskip = false, group = 'sales') => {
    setBusy(true); setError(null);
    const r = await skipDish(groupKey, unskip, group);
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    changed();
  };

  const proceed = async () => {
    if (!onProceed || proceedingRef.current || busy) return;
    if (preview) {
      setProceedError(`Confirm or cancel the ${preview.item_name} preview first.`);
      return;
    }
    proceedingRef.current = true;
    setProceeding(true); setProceedError(null);
    let r;
    try {
      r = await onProceed();
    } catch (err) {
      r = { ok: false, error: err?.message || 'Could not continue.' };
    } finally {
      proceedingRef.current = false;
      setProceeding(false);
    }
    if (r && r.ok) return;
    const left = (r && Array.isArray(r.dishes)) ? r.dishes : [];
    setProceedError(left.length
      ? `${left.length} dish${left.length === 1 ? '' : 'es'} still need${left.length === 1 ? 's' : ''} mapping or skipping: ${left.slice(0, 5).map((d) => d.item_name).join(', ')}${left.length > 5 ? '…' : ''}`
      : (r && r.error) || 'Could not continue.');
    load();
  };

  const groups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data?.groups || []).filter((g) => !q || g.item_name.toLowerCase().includes(q));
  }, [data, filter]);

  if (!open) return null;
  const settled = data?.settled || [];
  const skipped = data?.skipped || [];

  const locked = busy || proceeding;

  return (
    <div className="nim-overlay" onClick={locked ? undefined : onClose}>
      <div className="nim-container" role="dialog" aria-label="Not in Menu" onClick={(e) => e.stopPropagation()}>
        <div className="nim-header">
          <div>
            <h3 className="nim-title">Not in Menu</h3>
          </div>
          <button type="button" className="nim-close" aria-label="Close" onClick={onClose} disabled={locked}>×</button>
        </div>
        <div className="nim-body">
          {notice && <div className="um-notice" role="status">{notice}</div>}
          {error && <div className="um-error" role="alert">{error}</div>}
          {loadError && (
            <div className="um-error" role="alert">
              {loadError} <button type="button" className="um-undo-btn" onClick={load}>Retry</button>
            </div>
          )}
          {!data && !loadError && (
            <div className="nim-loading-state">
              <div className="nim-spinner" />
              <p className="nim-loading-text">Loading…</p>
            </div>
          )}
          {data && (
            <>
              <input className="nim-filter" type="search" placeholder="Filter dishes…" aria-label="Filter dishes"
                value={filter} onChange={(e) => setFilter(e.target.value)} />
              {SECTIONS.map(({ key, title }) => {
                const list = groups.filter((g) => sectionOf(g) === key);
                if (list.length === 0) return null;
                const readyInList = list.filter((g) => selectedCardKeys.has(cardKey(g)) && stagedMappings[cardKey(g)]).length;
                const allInListSelected = list.length > 0 && list.every((g) => selectedCardKeys.has(cardKey(g)));

                return (
                  <section key={key} className="nim-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <h4 className="nim-section-title" style={{ margin: 0 }}>{title} ({list.length})</h4>
                    </div>

                    {(key === 'pending' || key === 'mapped') && list.length > 0 && (
                      <div className="nim-batch-toolbar">
                        <div className="nim-batch-left">
                          <label className="nim-batch-check-label">
                            <input
                              type="checkbox"
                              checked={allInListSelected}
                              onChange={() => toggleSelectAll(list)}
                              disabled={busy}
                            />
                            <span>Select all ({list.length})</span>
                          </label>
                          <button
                            type="button"
                            className="nim-auto-suggest-btn"
                            onClick={() => autoSelectSuggestions(list)}
                            disabled={busy}
                            title="Auto-select top matching menu item suggestions for each dish"
                          >
                            Auto-select suggestions
                          </button>
                        </div>
                        <div className="nim-batch-right">
                          {readyInList > 0 && (
                            <button
                              type="button"
                              className="nim-batch-confirm-btn"
                              onClick={() => confirmBatch(false)}
                              disabled={busy}
                            >
                              {batchProcessing ? 'Confirming…' : `Confirm Mapped (${readyInList})`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {list.map((g) => {
                      const k = cardKey(g);
                      const staged = stagedMappings[k];
                      const isSelected = selectedCardKeys.has(k);

                      return (
                        <div key={k} className={`um-card ${staged || g.resolved_menu_item_id ? 'um-card-resolved' : ''}`}>
                          <div className="um-card-head">
                            <div className="um-card-head-left">
                              {(key === 'pending' || key === 'mapped') && (
                                <input
                                  type="checkbox"
                                  className="nim-card-checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleCardSelection(g)}
                                  disabled={busy}
                                  aria-label={`Select ${g.item_name}`}
                                />
                              )}
                              <div>
                                <div className="um-name">{g.item_name}</div>
                                <div className="nim-lane-tags">
                                  {lanesOf(g).map(([lane, label]) => (
                                    <span key={lane} className={`nim-lane-tag nim-lane-${lane}`}>{label}</span>
                                  ))}
                                </div>
                                <div className="um-sub">
                                  {g.occurrences} line{g.occurrences === 1 ? '' : 's'} · qty {g.total_quantity}
                                  {g.group !== 'banquet' && <> · {money(g.total_amount)}</>}
                                  {g.resolved_menu_item_name && <> · mapped to <strong>{g.resolved_menu_item_name}</strong></>}
                                </div>
                              </div>
                            </div>
                            {(key === 'pending' || key === 'mapped') && (
                              <button type="button" className="um-skip-btn" disabled={busy} onClick={() => skip(g.group_key, false, g.group || 'sales')}>Skip</button>
                            )}
                          </div>

                          {staged ? (
                            <div className="nim-staged-row">
                              <div className="nim-staged-info">
                                <span className="nim-staged-badge">✓ Mapped to</span>
                                <span className="nim-staged-name">{staged.menuItemName}</span>
                              </div>
                              <div className="nim-staged-actions">
                                <button
                                  type="button"
                                  className="um-undo-btn"
                                  disabled={busy}
                                  onClick={() => pick(g, { menu_item_id: staged.menuItemId, name: staged.menuItemName })}
                                  title="Inspect ingredients and preview deduction details"
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  className="um-undo-btn"
                                  disabled={busy}
                                  onClick={() => unstageMapping(g)}
                                  title="Change mapping"
                                >
                                  Change
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {g.resolved_menu_item_id && (
                                <button
                                  type="button"
                                  className="um-chip"
                                  disabled={busy}
                                  onClick={() => stageMapping(g, { menu_item_id: g.resolved_menu_item_id, name: g.resolved_menu_item_name })}
                                >
                                  Use saved: {g.resolved_menu_item_name}
                                </button>
                              )}
                              {key !== 'revenue_booked' && (
                                <MenuItemPicker
                                  menuItems={menu}
                                  suggestions={g.suggestions || []}
                                  disabled={busy}
                                  allowBanquet={g.group === 'banquet'}
                                  onPick={(item) => stageMapping(g, item)}
                                />
                              )}
                            </>
                          )}

                          {active === k && previewLoadingKey === k && (
                            <div className="nim-preview-loading">
                              <div className="nim-preview-spinner" />
                              <span>Loading ingredients preview…</span>
                            </div>
                          )}
                          {active === k && preview && previewLoadingKey !== k && (
                            <DishPreview
                              preview={preview}
                              busy={busy}
                              onConfirm={() => confirm(false)}
                              onCancel={() => { setActive(null); setPreview(null); setPreviewLoadingKey(null); }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </section>
                );
              })}
              {settled.length > 0 && (
                <section className="nim-section">
                  <h4 className="nim-section-title">Confirmed ({settled.length})</h4>
                  {settled.map((s) => (
                    <div key={cardKey(s)} className="um-card um-card-resolved">
                      <div className="um-card-head">
                        <div>
                          <div className="um-name">{s.item_name}</div>
                          <div className="um-sub">{s.lines} line{s.lines === 1 ? '' : 's'} · {money(s.total_amount)}{s.menu_item_name ? ` · ${s.menu_item_name}` : ''}</div>
                        </div>
                        <UndoDishButton code={s.undo?.code} reason={s.undo?.reason} disabled={busy} onClick={() => setUndoFor(s)} />
                      </div>
                    </div>
                  ))}
                </section>
              )}
              {skipped.length > 0 && (
                <section className="nim-section">
                  <h4 className="nim-section-title">Skipped ({skipped.length})</h4>
                  {skipped.map((s) => (
                    <div key={cardKey(s)} className="um-skipped-row">
                      <span>{s.item_name}{s.group === 'banquet' ? ' · Banquet' : ` · ${money(s.total_amount)}`}</span>
                      <button type="button" className="um-undo-btn" disabled={busy} onClick={() => skip(s.group_key, true, s.group || 'sales')}>Unskip</button>
                    </div>
                  ))}
                </section>
              )}
              {groups.length === 0 && settled.length === 0 && skipped.length === 0 && (
                <p className="um-muted">Nothing is waiting — every bill line matches a menu item.</p>
              )}
            </>
          )}
        </div>
        {onProceed && (
          <div className="nim-footer nim-proceed-footer">
            {proceedError && <div className="um-error nim-proceed-error" role="alert">{proceedError}</div>}
            <button type="button" className="um-primary-btn nim-proceed-btn"
              disabled={!data || locked} aria-busy={proceeding} onClick={proceed}>
              {proceeding ? 'Checking…' : 'Confirm & Proceed'}
            </button>
          </div>
        )}
      </div>
      {shortages && (
        <ShortageConsentModal
          shortages={normaliseShortages(shortages)}
          busy={busy}
          onConfirm={() => {
            if (preview) confirm(true);
            else confirmBatch(true);
          }}
          onCancel={() => setShortages(null)}
        />
      )}
      {undoFor && (
        <UndoDishConfirm
          groupKey={undoFor.group_key}
          group={undoFor.group || 'sales'}
          itemName={undoFor.item_name}
          onClose={() => setUndoFor(null)}
          onUndone={(r) => {
            setNotice(`${undoFor.item_name}: undone — stock put back, ${money(r.revenue_moved)} moved back to Not in Menu.`);
            setUndoFor(null);
            changed();
          }}
        />
      )}
    </div>
  );
}
